// Logic of the M-Bus and KNX tabs (ADR 0008), kept out of the components
// so it runs in the Node unit tests.
import { ApiError } from '../api/client.js';
import type {
  BundleState,
  EtsPreviewRow,
  KnxGroupAddress,
  KnxState,
  MbusScannedMeter,
  MbusState,
  ProtocolPoint,
  ProtocolRun,
} from '../api/protocol-types.js';

/** Driver default poll interval of M-Bus points (§5.2). */
export const MBUS_DEFAULT_POLL_MS = 15 * 60_000;

export function isStaleConfig(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'stale_config';
}

export const staleConfigMessage =
  'Someone changed this configuration meanwhile. The page shows the current state; apply your change again.';

export function isActiveRun(run: ProtocolRun | null | undefined): boolean {
  return run?.state === 'requested' || run?.state === 'running';
}

export type BundleBadge = 'pending' | 'sent' | 'applied' | 'rejected' | 'local';

/** One line about where the draft stands, with a badge kind. */
export function bundleSummary(
  managed: boolean,
  b: BundleState,
): { badge: BundleBadge; text: string } {
  if (b.pending) {
    return {
      badge: 'pending',
      text: managed
        ? 'Changes not sent to the edge yet — Apply sends them as a new configuration.'
        : 'Released: Apply hands this protocol back to the edge’s local file.',
    };
  }
  if (!b.section_in_bundle) {
    return { badge: 'local', text: 'The edge’s local file configures this protocol.' };
  }
  const v = b.version === null ? '' : ` (version ${String(b.version)})`;
  if (b.status === 'applied') return { badge: 'applied', text: `Running on the edge${v}.` };
  if (b.status === 'rejected') return { badge: 'rejected', text: `Rejected by the edge${v}.` };
  return { badge: 'sent', text: `Sent, waiting for the edge${v}.` };
}

const pointKey = (device: string, metric: string) => `${device}/${metric}`;

/** Point ids by `device/metric`. */
export function pointIndex(points: readonly ProtocolPoint[]): Map<string, string> {
  return new Map(points.map((p) => [pointKey(p.device_id, p.metric), p.point_id]));
}

// --- M-Bus ----------------------------------------------------------------------

export interface RecordView {
  metric: string;
  description: string;
  unit: string;
  data_type: string;
  /** Value of the last scan. */
  scan_value: number | string | null;
  error: string | null;
  selected: boolean;
  interval_ms: number | null;
  point_id: string | null;
}

export interface MeterView {
  device_id: string;
  /** Converter in the draft, else the one the scan found it on. */
  converter: string;
  primary: number | null;
  name: string | null;
  configured: boolean;
  scanned: MbusScannedMeter | null;
  records: RecordView[];
}

const STATUS = 'meter/status';

/**
 * Configured meters (draft order) and then meters only found by scans,
 * each with the scanned records plus the selected ones the scan did not
 * report, and `meter/status`.
 */
export function meterViews(state: MbusState): MeterView[] {
  const scanned = new Map(state.meters.map((m) => [m.device_id, m]));
  const ids = pointIndex(state.points);
  const selected = new Map(state.draft.points.map((p) => [pointKey(p.device_id, p.metric), p]));
  const view = (
    device: string,
    base: Omit<MeterView, 'records' | 'scanned' | 'device_id'>,
  ): MeterView => {
    const scan = scanned.get(device) ?? null;
    const records: RecordView[] = (scan?.records ?? []).map((r) => {
      const sel = selected.get(pointKey(device, r.metric));
      return {
        metric: r.metric,
        description: r.description,
        unit: r.unit,
        data_type: r.data_type,
        scan_value: r.value,
        error: r.error,
        selected: sel !== undefined,
        interval_ms: sel?.interval_ms ?? null,
        point_id: ids.get(pointKey(device, r.metric)) ?? null,
      };
    });
    const listed = new Set(records.map((r) => r.metric));
    for (const p of state.draft.points) {
      if (p.device_id !== device || listed.has(p.metric)) continue;
      listed.add(p.metric);
      records.push({
        metric: p.metric,
        description: p.object_name,
        unit: p.units,
        data_type: p.metric === STATUS ? 'UInt32' : 'Double',
        scan_value: null,
        error: null,
        selected: true,
        interval_ms: p.interval_ms,
        point_id: ids.get(pointKey(device, p.metric)) ?? null,
      });
    }
    if (!listed.has(STATUS)) {
      records.push({
        metric: STATUS,
        description: 'Meter status',
        unit: '',
        data_type: 'UInt32',
        scan_value: null,
        error: null,
        selected: false,
        interval_ms: null,
        point_id: ids.get(pointKey(device, STATUS)) ?? null,
      });
    }
    return { device_id: device, ...base, scanned: scan, records };
  };
  const out: MeterView[] = [];
  const done = new Set<string>();
  for (const c of state.draft.converters) {
    for (const m of c.meters) {
      done.add(m.device_id);
      out.push(
        view(m.device_id, { converter: c.id, primary: m.primary, name: m.name, configured: true }),
      );
    }
  }
  for (const m of state.meters) {
    if (done.has(m.device_id)) continue;
    out.push(
      view(m.device_id, {
        converter: m.converter,
        primary: m.primary_address,
        name: null,
        configured: false,
      }),
    );
  }
  return out;
}

/** `30` → 30 min in ms; '' → null (driver default); 'x' → undefined (invalid). */
export function parseMinutes(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d+(\.\d+)?$/.test(t)) return undefined;
  const ms = Math.round(Number(t) * 60_000);
  return ms >= 1000 && ms <= 86_400_000 ? ms : undefined;
}

export function formatMinutes(ms: number | null): string {
  return ms === null ? '' : String(Math.round((ms / 60_000) * 100) / 100);
}

/** `0-250`, `5`, `` (all) → a primary address range; null when invalid. */
export function parsePrimaryRange(text: string): { from: number; to: number } | null {
  const t = text.trim();
  if (t === '') return { from: 0, to: 250 };
  const m = /^(\d{1,3})(?:\s*-\s*(\d{1,3}))?$/.exec(t);
  if (!m?.[1]) return null;
  const from = Number(m[1]);
  const to = m[2] === undefined ? from : Number(m[2]);
  return from <= to && to <= 250 ? { from, to } : null;
}

/** 8 digits with F wildcards (secondary search start mask). */
export function isScanMask(text: string): boolean {
  return /^[0-9Ff]{8}$/.test(text.trim());
}

/** `12345678 KAM v27 heat` style label of a scanned meter. */
export function meterLabel(m: MeterView): string {
  const s = m.scanned?.secondary;
  const what = m.scanned?.medium_text;
  if (!s) return m.device_id;
  return `${s.id} ${s.manufacturer}${what ? ` · ${what}` : ''}`;
}

// --- KNX ------------------------------------------------------------------------

export const knxDevice = (ifaceId: string) => `knx-${ifaceId}`;

export interface GaRow extends KnxGroupAddress {
  point_id: string | null;
}

/** The group addresses of an interface with their point ids, filtered by `search`. */
export function gaRows(state: KnxState, ifaceId: string, search = ''): GaRow[] {
  const iface = state.draft.interfaces.find((i) => i.id === ifaceId);
  if (!iface) return [];
  const ids = pointIndex(state.points);
  const q = search.trim().toLowerCase();
  return iface.group_addresses
    .filter(
      (g) =>
        q === '' ||
        g.address.includes(q) ||
        g.dpt.includes(q) ||
        (g.name ?? '').toLowerCase().includes(q) ||
        (g.description ?? '').toLowerCase().includes(q),
    )
    .map((g) => ({ ...g, point_id: ids.get(`${knxDevice(ifaceId)}/GA:${g.address}`) ?? null }));
}

/** `1/2/3` (also `1-2-3`): three-level group address, else null. */
export function parseGroupAddress(text: string): string | null {
  const m = /^(\d{1,2})[/-](\d)[/-](\d{1,3})$/.exec(text.trim());
  if (!m?.[1] || !m[2] || !m[3]) return null;
  const [a, b, c] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return a <= 31 && b <= 7 && c <= 255 ? `${String(a)}/${String(b)}/${String(c)}` : null;
}

/**
 * DPTs that fit a telegram payload (hex, as the edge reports it) — a hint
 * for the picker, never a decision: 1 byte holding 0/1 is usually a switch,
 * 2 bytes a KNX float (temperature, lux …), 4 bytes a counter or IEEE float.
 */
export function suggestDpts(rawHex: string): string[] {
  const bytes = rawHex.replace(/\s+/g, '').length / 2;
  const first = Number.parseInt(rawHex.slice(0, 2) || '0', 16);
  switch (bytes) {
    case 0:
    case 1:
      return first <= 1 ? ['1.001', '1.002', '5.001'] : ['5.001', '5.010', '6.010', '17.001'];
    case 2:
      return ['9.001', '9.004', '9.007', '7.001', '8.001'];
    case 3:
      return ['10.001', '11.001', '232.600'];
    case 4:
      return ['14.056', '13.013', '12.001', '14.019'];
    case 8:
      return ['19.001', '29.010'];
    case 14:
      return ['16.000', '16.001'];
    default:
      return [];
  }
}

/** Rows an ETS merge takes by default: new and changed ones. */
export function etsDefaultSelection(rows: readonly EtsPreviewRow[]): Set<string> {
  return new Set(
    rows.filter((r) => r.status === 'new' || r.status === 'changed').map((r) => r.address),
  );
}

/** Base64 of file bytes (the API takes the export as base64, any text encoding). */
export function toBase64(bytes: Uint8Array): string {
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(s);
}
